/**
 * Service-related functions for billing system
 * Handles service selection and anode location management
 */

import { initAutoSuggest } from './inline-customer-suggest.js';

/**
 * Select an anode location and show position modal
 * @param {string} location - The anode location (shaft, prop, hull, etc.)
 */
export function selectAnodeLocation(location) {
    console.log(`📍 Selecting anode location: ${location}`);

    // Create position selection modal
    const modalHTML = `
        <div class="modal-overlay" id="positionSelectionModal" onclick="window.__closePositionModal(event)">
            <div class="anode-modal-content" onclick="event.stopPropagation()" style="max-width: 400px;">
                <div class="anode-modal-header">
                    <h3>Select Position for ${location.charAt(0).toUpperCase() + location.slice(1)}</h3>
                    <button onclick="window.__closePositionModal()" class="anode-modal-close">&times;</button>
                </div>
                <div class="anode-modal-body">
                    <div style="display: flex; flex-direction: row; gap: 15px; justify-content: space-between;">
                        <button class="position-select-btn" onclick="window.__selectPosition('${location}', 'port')" style="flex: 1;">
                            Port
                        </button>
                        <button class="position-select-btn" onclick="window.__selectPosition('${location}', 'centerline')" style="flex: 1;">
                            Centerline
                        </button>
                        <button class="position-select-btn" onclick="window.__selectPosition('${location}', 'starboard')" style="flex: 1;">
                            Starboard
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    // Remove any existing position modal
    const existingModal = document.getElementById('positionSelectionModal');
    if (existingModal) {
        existingModal.remove();
    }

    // Add modal to page
    document.body.insertAdjacentHTML('beforeend', modalHTML);
}

/**
 * Close the position selection modal
 */
function closePositionModal(event) {
    if (event && event.target !== event.currentTarget) return;

    const modal = document.getElementById('positionSelectionModal');
    if (modal) {
        modal.remove();
    }
}

/**
 * Handle position selection and create anode assignment
 * @param {string} location - The anode location
 * @param {string} position - The position (port, starboard, center)
 */
function selectPosition(location, position) {
    console.log(`✅ Position selected: ${location} - ${position}`);

    // Close the position modal
    closePositionModal();

    // Create a unique ID for this assignment
    const assignmentId = `${location}-${position}-${Date.now()}`;

    // Initialize anodeAssignments if it doesn't exist
    if (!window.anodeAssignments) {
        window.anodeAssignments = {};
    }

    // Create the assignment object
    window.anodeAssignments[assignmentId] = {
        location: location,
        position: position,
        condition: 100,  // Default to 100%
        status: 'inspected',  // Default status
        anode_id: null,
        anode_name: null,
        anode_price: null
    };

    console.log('Anode assignment created:', window.anodeAssignments[assignmentId]);

    // Update the UI
    if (typeof window.updateAnodeAssignmentsList === 'function') {
        window.updateAnodeAssignmentsList();
    }

    if (typeof window.updateLocationCounts === 'function') {
        window.updateLocationCounts();
    }

    // Trigger charge summary update to show new anode
    if (typeof window.updateChargeSummary === 'function') {
        window.updateChargeSummary();
    }
    if (typeof window.updateWizardPricing === 'function') {
        window.updateWizardPricing();
    }

    // Auto-open the anode catalog modal for this assignment
    if (typeof window.openAnodeCatalogModal === 'function') {
        console.log('Auto-opening anode catalog modal...');
        window.openAnodeCatalogModal(location, position, (anodeData) => {
            console.log('Anode selected from catalog:', anodeData);
            window.anodeAssignments[assignmentId].anode_id = anodeData.anode_id;
            window.anodeAssignments[assignmentId].anode_name = anodeData.anode_name;
            window.anodeAssignments[assignmentId].anode_price = anodeData.anode_price;

            // Update UI after selection
            if (typeof window.updateAnodeAssignmentsList === 'function') {
                window.updateAnodeAssignmentsList();
            }

            // Trigger charge summary update after anode selection
            if (typeof window.updateChargeSummary === 'function') {
                window.updateChargeSummary();
            }
            if (typeof window.updateWizardPricing === 'function') {
                window.updateWizardPricing();
            }
        });
    }
}

/**
 * Render the consolidated form based on service type
 * @param {boolean} isCleaningService - Whether this is a cleaning service
 * @param {string} serviceKey - The service key (recurring_cleaning, onetime_cleaning, anodes_only, etc.)
 */
export function renderConsolidatedForm(isCleaningService, serviceKey) {
    console.log(`📋 Rendering consolidated form for service: ${serviceKey}, isCleaningService: ${isCleaningService}`);

    // Show boat configuration section
    renderBoatConfigSection();

    // Get the conditions section
    const conditionsSection = document.getElementById('conditionsSection');

    if (!conditionsSection) {
        console.error('conditionsSection not found in DOM');
        return;
    }

    // Show the conditions section
    conditionsSection.style.display = 'block';

    // Different behavior based on service type
    if (serviceKey === 'anodes_only') {
        // For anodes only, focus on anode locations
        console.log('Anodes Only service - showing anode locations');

        // Scroll to customer/boat form, not the anode section
        setTimeout(() => {
            const boatConfig = document.getElementById('dynamicBoatConfig');
            if (boatConfig) {
                boatConfig.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                // Fallback to anode section if boat config not found
                const anodeSection = document.querySelector('.anode-locations-manager');
                if (anodeSection) {
                    anodeSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }, 300);

    } else if (isCleaningService) {
        // For cleaning services, focus on paint/growth conditions
        console.log('Cleaning service - mounting React conditions form');

        // Mount React conditions form with default values
        if (typeof window.mountConditionsForm === 'function') {
            window.mountConditionsForm('reactConditionsMount', 0, 0);
        } else {
            console.warn('React mountConditionsForm not available - conditions form may not render');
        }

    } else {
        // Other services
        console.log('Other service type:', serviceKey);
    }

    // Update pricing if available
    if (typeof window.updatePricing === 'function') {
        window.updatePricing();
    }

    // Update charge summary if available
    if (typeof window.updateChargeSummary === 'function') {
        window.updateChargeSummary();
    }

    console.log('✅ Consolidated form rendered successfully');
}

/**
 * Render the boat configuration section
 */
function renderBoatConfigSection() {
    // Check if already rendered
    let boatConfigContainer = document.getElementById('dynamicBoatConfig');

    if (boatConfigContainer) {
        boatConfigContainer.style.display = 'block';
        return;
    }

    // Create the boat configuration section
    const configHTML = `
        <div id="dynamicBoatConfig" class="boat-config-container" style="background: white; padding: 20px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
                <h3 style="margin: 0; color: #2c3e50; font-size: 18px;">👤 Customer Information</h3>
                <button id="editCustomerBtn" onclick="window.__toggleCustomerEdit()"
                        style="display: none; padding: 6px 12px; background: #3498db; color: white; border: none; border-radius: 4px; font-size: 14px; cursor: pointer; font-weight: 500;">
                    ✏️ Edit
                </button>
            </div>

            <!-- Customer Name -->
            <div style="margin-bottom: 15px;">
                <label style="display: block; margin-bottom: 5px; font-weight: 600; color: #34495e;">Customer Name</label>
                <input type="text" id="wizardCustomerName" placeholder="Enter customer name"
                       style="width: 100%; padding: 10px; border: 1px solid #bdc3c7; border-radius: 5px; font-size: 16px;">
            </div>

            <!-- Customer Email -->
            <div style="margin-bottom: 15px;">
                <label style="display: block; margin-bottom: 5px; font-weight: 600; color: #34495e;">Customer Email</label>
                <input type="email" id="wizardCustomerEmail" placeholder="customer@example.com"
                       style="width: 100%; padding: 10px; border: 1px solid #bdc3c7; border-radius: 5px; font-size: 16px;">
            </div>

            <!-- Customer Phone -->
            <div style="margin-bottom: 15px;">
                <label style="display: block; margin-bottom: 5px; font-weight: 600; color: #34495e;">Customer Phone</label>
                <input type="tel" id="wizardCustomerPhone" placeholder="(555) 123-4567"
                       style="width: 100%; padding: 10px; border: 1px solid #bdc3c7; border-radius: 5px; font-size: 16px;">
            </div>

            <hr style="border: none; border-top: 1px solid #ecf0f1; margin: 20px 0;">

            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
                <h3 style="margin: 0; color: #2c3e50; font-size: 18px;">⛵ Boat Information</h3>
                <button id="editBoatBtn" onclick="window.__toggleBoatEdit()"
                        style="display: none; padding: 6px 12px; background: #3498db; color: white; border: none; border-radius: 4px; font-size: 14px; cursor: pointer; font-weight: 500;">
                    ✏️ Edit
                </button>
            </div>

            <!-- Boat Name -->
            <div style="margin-bottom: 15px;">
                <label style="display: block; margin-bottom: 5px; font-weight: 600; color: #34495e;">Boat Name</label>
                <input type="text" id="wizardBoatName" placeholder="Enter boat name"
                       style="width: 100%; padding: 10px; border: 1px solid #bdc3c7; border-radius: 5px; font-size: 16px;">
            </div>

            <!-- Boat Length -->
            <div style="margin-bottom: 20px;">
                <label style="display: block; margin-bottom: 5px; font-weight: 600; color: #34495e;">Boat Length (feet)</label>
                <input type="number" id="wizardBoatLength" min="10" max="200" value="35"
                       style="width: 100%; padding: 10px; border: 1px solid #bdc3c7; border-radius: 5px; font-size: 16px;"
                       oninput="if(window.updateWizardPricing) window.updateWizardPricing();">
            </div>

            <!-- Boat Type -->
            <div style="margin-bottom: 20px;">
                <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #34495e;">Boat Type</label>
                <div style="display: flex; gap: 12px;">
                    <button class="boat-type-btn selected" data-value="sailboat"
                            onclick="window.__selectBoatType('sailboat')"
                            style="flex: 1; min-height: 60px; padding: 16px 24px; border: 2px solid #4CAF50; background: #4CAF50; color: white; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s ease;">
                        ⛵ Sailboat
                    </button>
                    <button class="boat-type-btn" data-value="powerboat"
                            onclick="window.__selectBoatType('powerboat')"
                            style="flex: 1; min-height: 60px; padding: 16px 24px; border: 2px solid #4CAF50; background: white; color: #4CAF50; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s ease;">
                        🚤 Powerboat (+25%)
                    </button>
                </div>
            </div>

            <!-- Hull Type -->
            <div style="margin-bottom: 20px;">
                <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #34495e;">Hull Type</label>
                <div style="display: flex; gap: 12px; flex-wrap: wrap;">
                    <button class="hull-type-btn selected" data-value="monohull"
                            onclick="window.__selectHullType('monohull')"
                            style="flex: 1; min-width: 150px; min-height: 60px; padding: 16px 24px; border: 2px solid #4CAF50; background: #4CAF50; color: white; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s ease;">
                        Monohull
                    </button>
                    <button class="hull-type-btn" data-value="catamaran"
                            onclick="window.__selectHullType('catamaran')"
                            style="flex: 1; min-width: 150px; min-height: 60px; padding: 16px 24px; border: 2px solid #4CAF50; background: white; color: #4CAF50; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s ease;">
                        Catamaran (+25%)
                    </button>
                    <button class="hull-type-btn" data-value="trimaran"
                            onclick="window.__selectHullType('trimaran')"
                            style="flex: 1; min-width: 150px; min-height: 60px; padding: 16px 24px; border: 2px solid #4CAF50; background: white; color: #4CAF50; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s ease;">
                        Trimaran (+50%)
                    </button>
                </div>
            </div>

            <!-- Propeller Count -->
            <div style="margin-bottom: 20px;">
                <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #34495e;">Number of Propellers</label>
                <select id="wizardPropellerCount"
                        style="width: 100%; padding: 10px; border: 1px solid #bdc3c7; border-radius: 5px; font-size: 16px; cursor: pointer;"
                        onchange="if(window.updateWizardPricing) window.updateWizardPricing();">
                    <option value="0" selected>0 - No Propellers</option>
                    <option value="1">1 - Single Propeller</option>
                    <option value="2">2 - Twin Propellers</option>
                    <option value="3">3 - Triple Propellers</option>
                    <option value="4">4 - Quad Propellers</option>
                    <option value="5">5 - Five Propellers</option>
                </select>
                <p style="font-size: 12px; color: #7f8c8d; margin: 5px 0 0 0;">
                    <em>Note: Propellers beyond the first may incur a surcharge</em>
                </p>
            </div>
        </div>
    `;

    // Find where to insert it - before the conditions section
    const conditionsSection = document.getElementById('conditionsSection');
    if (conditionsSection) {
        conditionsSection.insertAdjacentHTML('beforebegin', configHTML);
        console.log('✅ Boat configuration section created');

        // Pre-fill from selectedCustomer/selectedBoat if available
        setTimeout(() => {
            // Initialize auto-suggest on customer/boat fields
            console.log('🔧 Initializing inline auto-suggest...');
            initAutoSuggest('wizardCustomerName', 'name');
            initAutoSuggest('wizardCustomerEmail', 'email');
            initAutoSuggest('wizardBoatName', 'boat_name');
            console.log('✅ Auto-suggest initialized on wizard fields');

            // Populate customer fields
            const customerNameInput = document.getElementById('wizardCustomerName');
            const customerEmailInput = document.getElementById('wizardCustomerEmail');
            const customerPhoneInput = document.getElementById('wizardCustomerPhone');
            const editCustomerBtn = document.getElementById('editCustomerBtn');

            let customerFieldsLocked = false;
            if (window.selectedCustomer) {
                if (customerNameInput && window.selectedCustomer.name) {
                    customerNameInput.value = window.selectedCustomer.name;
                    lockField(customerNameInput);
                    customerFieldsLocked = true;
                }
                if (customerEmailInput && window.selectedCustomer.email) {
                    customerEmailInput.value = window.selectedCustomer.email;
                    lockField(customerEmailInput);
                    customerFieldsLocked = true;
                }
                if (customerPhoneInput && window.selectedCustomer.phone) {
                    customerPhoneInput.value = window.selectedCustomer.phone;
                    lockField(customerPhoneInput);
                    customerFieldsLocked = true;
                }

                // Show edit button if any customer fields were populated
                if (customerFieldsLocked && editCustomerBtn) {
                    editCustomerBtn.style.display = 'block';
                }
            }

            // Populate boat fields
            const boatNameInput = document.getElementById('wizardBoatName');
            const boatLengthInput = document.getElementById('wizardBoatLength');
            const propellerCountInput = document.getElementById('wizardPropellerCount');
            const editBoatBtn = document.getElementById('editBoatBtn');

            let boatFieldsLocked = false;
            if (window.selectedCustomer?.boat_name && boatNameInput) {
                boatNameInput.value = window.selectedCustomer.boat_name;
                lockField(boatNameInput);
                boatFieldsLocked = true;
            } else if (window.selectedBoat?.name && boatNameInput) {
                boatNameInput.value = window.selectedBoat.name;
                lockField(boatNameInput);
                boatFieldsLocked = true;
            }

            if (boatLengthInput) {
                if (window.selectedCustomer?.boat_length) {
                    boatLengthInput.value = window.selectedCustomer.boat_length;
                    lockField(boatLengthInput);
                    boatFieldsLocked = true;
                } else if (window.selectedBoat?.length) {
                    boatLengthInput.value = window.selectedBoat.length;
                    lockField(boatLengthInput);
                    boatFieldsLocked = true;
                }
            }

            // Populate propeller count from boat data
            if (propellerCountInput && window.selectedBoat?.propeller_count !== undefined) {
                propellerCountInput.value = window.selectedBoat.propeller_count;
            }

            // Show edit button if any boat fields were populated
            if (boatFieldsLocked && editBoatBtn) {
                editBoatBtn.style.display = 'block';
            }

            // Sync with hidden inputs
            syncBoatConfigToHiddenInputs();

            // Trigger initial pricing calculation
            if (window.updateWizardPricing) {
                window.updateWizardPricing();
            }
        }, 100);
    } else {
        console.error('Could not find conditionsSection to insert boat config');
    }
}

/**
 * Sync boat configuration to hidden form inputs
 */
function syncBoatConfigToHiddenInputs() {
    const boatLength = document.getElementById('wizardBoatLength')?.value;
    const boatType = document.querySelector('.boat-type-btn.selected')?.getAttribute('data-value');
    const hullType = document.querySelector('.hull-type-btn.selected')?.getAttribute('data-value');
    const twinEngines = document.getElementById('wizard_twin_engines')?.checked;
    const propellerCount = document.getElementById('wizardPropellerCount')?.value;

    // Sync to hidden inputs
    const hiddenBoatLength = document.getElementById('boatLength');
    if (hiddenBoatLength && boatLength) {
        hiddenBoatLength.value = boatLength;
    }

    if (boatType) {
        const hiddenBoatType = document.querySelector(`input[name="boat_type"][value="${boatType}"]`);
        if (hiddenBoatType) {
            hiddenBoatType.checked = true;
        }
    }

    if (hullType) {
        const hiddenHullType = document.querySelector(`input[name="hull_type"][value="${hullType}"]`);
        if (hiddenHullType) {
            hiddenHullType.checked = true;
        }
    }

    if (twinEngines !== undefined) {
        const hiddenTwinEngines = document.getElementById('has_twin_engines');
        if (hiddenTwinEngines) {
            hiddenTwinEngines.checked = twinEngines;
        }
    }

    // Store propeller count in window object for access by other functions
    if (propellerCount !== undefined) {
        window.selectedPropellerCount = parseInt(propellerCount);
    }
}


/**
 * Lock a field (disable and grey background)
 * @param {HTMLElement} field - The input field to lock
 */
function lockField(field) {
    if (!field) return;
    field.disabled = true;
    field.style.background = '#f5f5f5';
    field.style.color = '#666';
    field.style.cursor = 'not-allowed';
}

/**
 * Unlock a field (enable and normal background)
 * @param {HTMLElement} field - The input field to unlock
 */
function unlockField(field) {
    if (!field) return;
    field.disabled = false;
    field.style.background = 'white';
    field.style.color = '#2c3e50';
    field.style.cursor = 'text';
}

/**
 * Toggle edit mode for customer fields
 */
function toggleCustomerEdit() {
    console.log('✏️ Toggling customer edit mode');

    const customerNameInput = document.getElementById('wizardCustomerName');
    const customerEmailInput = document.getElementById('wizardCustomerEmail');
    const customerPhoneInput = document.getElementById('wizardCustomerPhone');
    const editBtn = document.getElementById('editCustomerBtn');

    const isLocked = customerNameInput?.disabled;

    if (isLocked) {
        // Unlock fields
        unlockField(customerNameInput);
        unlockField(customerEmailInput);
        unlockField(customerPhoneInput);
        if (editBtn) {
            editBtn.textContent = '🔒 Lock';
            editBtn.style.background = '#95a5a6';
        }
        console.log('✅ Customer fields unlocked');
    } else {
        // Lock fields
        lockField(customerNameInput);
        lockField(customerEmailInput);
        lockField(customerPhoneInput);
        if (editBtn) {
            editBtn.textContent = '✏️ Edit';
            editBtn.style.background = '#3498db';
        }
        console.log('✅ Customer fields locked');
    }
}

/**
 * Toggle edit mode for boat fields
 */
function toggleBoatEdit() {
    console.log('✏️ Toggling boat edit mode');

    const boatNameInput = document.getElementById('wizardBoatName');
    const boatLengthInput = document.getElementById('wizardBoatLength');
    const editBtn = document.getElementById('editBoatBtn');

    const isLocked = boatNameInput?.disabled;

    if (isLocked) {
        // Unlock fields
        unlockField(boatNameInput);
        unlockField(boatLengthInput);
        if (editBtn) {
            editBtn.textContent = '🔒 Lock';
            editBtn.style.background = '#95a5a6';
        }
        console.log('✅ Boat fields unlocked');
    } else {
        // Lock fields
        lockField(boatNameInput);
        lockField(boatLengthInput);
        if (editBtn) {
            editBtn.textContent = '✏️ Edit';
            editBtn.style.background = '#3498db';
        }
        console.log('✅ Boat fields locked');
    }
}

/**
 * Handle boat type button selection
 * @param {string} type - The boat type (sailboat, powerboat)
 */
function selectBoatType(type) {
    console.log('🚤 Selecting boat type:', type);

    // Remove 'selected' class from all boat type buttons
    document.querySelectorAll('.boat-type-btn').forEach(btn => {
        btn.classList.remove('selected');
        btn.style.background = 'white';
        btn.style.color = '#4CAF50';
    });

    // Add 'selected' class to clicked button
    const selectedBtn = document.querySelector(`.boat-type-btn[data-value="${type}"]`);
    if (selectedBtn) {
        selectedBtn.classList.add('selected');
        selectedBtn.style.background = '#4CAF50';
        selectedBtn.style.color = 'white';
    }

    // Update pricing
    if (window.updateWizardPricing) {
        window.updateWizardPricing();
    }
}

/**
 * Handle hull type button selection
 * @param {string} type - The hull type (monohull, catamaran, trimaran)
 */
function selectHullType(type) {
    console.log('⛵ Selecting hull type:', type);

    // Remove 'selected' class from all hull type buttons
    document.querySelectorAll('.hull-type-btn').forEach(btn => {
        btn.classList.remove('selected');
        btn.style.background = 'white';
        btn.style.color = '#4CAF50';
    });

    // Add 'selected' class to clicked button
    const selectedBtn = document.querySelector(`.hull-type-btn[data-value="${type}"]`);
    if (selectedBtn) {
        selectedBtn.classList.add('selected');
        selectedBtn.style.background = '#4CAF50';
        selectedBtn.style.color = 'white';
    }

    // Update pricing
    if (window.updateWizardPricing) {
        window.updateWizardPricing();
    }
}

// Export to window object for inline onclick handlers
window.selectAnodeLocation = selectAnodeLocation;
window.__closePositionModal = closePositionModal;
window.__selectPosition = selectPosition;
window.renderConsolidatedForm = renderConsolidatedForm;
window.__selectBoatType = selectBoatType;
window.__selectHullType = selectHullType;
window.__toggleCustomerEdit = toggleCustomerEdit;
window.__toggleBoatEdit = toggleBoatEdit;

console.log('✅ Service functions module loaded');
